import { Platform } from "react-native";

/**
 * Nur das Aktiv-Flag, je Nutzer. Der Entwurf bleibt im Speicher der laufenden Sitzung.
 * Web: localStorage, damit andere Tabs das Storage-Ereignis sehen.
 * Nativ: AsyncStorage, derselbe Schlüssel.
 */
export const FINDER_LOCK_PREFIX = "ferina.finderMode.active.";

export function finderLockKey(userId: string): string {
  return `${FINDER_LOCK_PREFIX}${userId}`;
}

/** true/false nach einem erfolgreichen Lesen oder Schreiben. "error" = Lesen gescheitert. */
type SessionLock = boolean | "error";

const sessionLocks = new Map<string, SessionLock>();

/**
 * Dieser Tab ist offen, obwohl der Speicher die Sperre nicht loswurde.
 * Ein Prozessneustart vergisst das und liest den Speicher neu.
 */
const unlockedDespiteStorage = new Set<string>();

/** Simuliert einen Prozessneustart: Storage bleibt, der Speicher der Sitzung nicht. */
export function forgetFinderLockSession(): void {
  sessionLocks.clear();
  unlockedDespiteStorage.clear();
}

const clearedListeners = new Set<(userId: string) => void>();

export function onFinderLockCleared(listener: (userId: string) => void): () => void {
  clearedListeners.add(listener);
  return () => {
    clearedListeners.delete(listener);
  };
}

type StorageListener = (userId: string, locked: boolean) => void;

const storageListeners = new Set<StorageListener>();

function onStorageEvent(event: { key?: string | null; newValue?: string | null }) {
  const key = event?.key;
  if (!key || !key.startsWith(FINDER_LOCK_PREFIX)) return;
  const userId = key.slice(FINDER_LOCK_PREFIX.length);
  if (!userId) return;
  const locked = event.newValue === "1";
  for (const listener of storageListeners) listener(userId, locked);
}

let boundWindow: EventTarget | null = null;

function ensureStorageListener(): void {
  if (Platform.OS !== "web") return;
  const win = globalThis.window;
  if (!win?.addEventListener || win === boundWindow) return;
  if (boundWindow && "removeEventListener" in boundWindow) {
    boundWindow.removeEventListener("storage", onStorageEvent as EventListener);
  }
  boundWindow = win;
  win.addEventListener("storage", onStorageEvent as EventListener);
}

/** Andere Tabs: localStorage feuert storage nur außerhalb des schreibenden Tabs. */
export function onFinderLockStorage(listener: StorageListener): () => void {
  storageListeners.add(listener);
  ensureStorageListener();
  return () => {
    storageListeners.delete(listener);
  };
}

function webStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Web sofort: true/false, bei Lesefehler true (zugesperrt).
 * Nativ null, solange AsyncStorage für diese Nutzer-ID noch nicht gelesen wurde.
 * Ohne Nutzer-ID null: die Hülle bleibt zu, bis die Sitzung feststeht.
 */
export function readFinderLockSync(userId: string | null): boolean | null {
  if (!userId) return null;
  if (unlockedDespiteStorage.has(userId)) return false;
  if (Platform.OS === "web") {
    try {
      const storage = webStorage();
      if (!storage) return true;
      return storage.getItem(finderLockKey(userId)) === "1";
    } catch {
      return true;
    }
  }
  if (!sessionLocks.has(userId)) return null;
  const value = sessionLocks.get(userId);
  if (value === "error") return true;
  return value === true;
}

/** Lesefehler zählt als gesperrt und wird nicht als „offen“ gemerkt. */
export async function readFinderLock(userId: string): Promise<boolean> {
  if (unlockedDespiteStorage.has(userId)) return false;
  if (Platform.OS === "web") return readFinderLockSync(userId) !== false;
  const cached = sessionLocks.get(userId);
  if (cached === true || cached === false) return cached;
  try {
    const AsyncStorage = (await import("@react-native-async-storage/async-storage")).default;
    const value = await AsyncStorage.getItem(finderLockKey(userId));
    const locked = value === "1";
    sessionLocks.set(userId, locked);
    return locked;
  } catch {
    sessionLocks.set(userId, "error");
    return true;
  }
}

/** Synchron auf Web. false, wenn der Speicher fehlt oder wirft. */
export function writeFinderLockSync(userId: string, locked: boolean): boolean {
  try {
    const storage = webStorage();
    if (!storage) return false;
    const key = finderLockKey(userId);
    if (locked) storage.setItem(key, "1");
    else storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/** Nativ wartet auf AsyncStorage. Ein Schreibfehler setzt die Sitzung nicht auf gesperrt. */
export async function writeFinderLock(userId: string, locked: boolean): Promise<boolean> {
  if (Platform.OS === "web") return writeFinderLockSync(userId, locked);
  try {
    const AsyncStorage = (await import("@react-native-async-storage/async-storage")).default;
    const key = finderLockKey(userId);
    if (locked) await AsyncStorage.setItem(key, "1");
    else await AsyncStorage.removeItem(key);
    sessionLocks.set(userId, locked);
    return true;
  } catch {
    return false;
  }
}

function notifyCleared(userId: string): void {
  for (const listener of clearedListeners) listener(userId);
}

/** Nach einem gelungenen Setzen der Sperre gilt der Speicher wieder. */
export function noteFinderLockHeld(userId: string): void {
  unlockedDespiteStorage.delete(userId);
}

/**
 * Rückgabe an die Pflegekraft und jedes SIGNED_OUT.
 * Der Speicher dieses Tabs ist sofort frei. Ein hängendes oder
 * fehlgeschlagenes Löschen blockiert die Rückgabe nicht.
 * true, wenn der Gerätespeicher die Sperre losgeworden ist.
 */
export function clearFinderModeLock(userId: string | null | undefined): Promise<boolean> {
  if (!userId) return Promise.resolve(true);
  sessionLocks.set(userId, false);
  unlockedDespiteStorage.add(userId);
  notifyCleared(userId);
  if (Platform.OS === "web") {
    const ok = writeFinderLockSync(userId, false);
    if (ok) unlockedDespiteStorage.delete(userId);
    return Promise.resolve(ok);
  }
  return writeFinderLock(userId, false).then((ok) => {
    if (ok) unlockedDespiteStorage.delete(userId);
    else sessionLocks.set(userId, false);
    return ok;
  });
}

/** Test-Hilfe: Sitzungsspeicher leeren, ohne Hörer zu rufen. */
export function resetFinderLockStateForTests(): void {
  sessionLocks.clear();
  unlockedDespiteStorage.clear();
  try {
    const storage = webStorage();
    if (!storage) return;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(FINDER_LOCK_PREFIX)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    /* privater Modus oder Test-Doppel */
  }
}
